import React, { useState, useEffect, useCallback, useRef } from "react";
import Grid from "@mui/material/Grid";
import styles from "../paymentThreshold/PaymentThreshold.module.scss";
import { withTranslation } from "react-i18next";
import EditIcon from "../../../shared/Edit/EditIcon";
import {
  TextValidator,
  ValidatorForm,
} from "react-material-ui-form-validator";
import MarketPreferencesClient from "../../../shared/clients/MarketPreferencesClient";
import HasPermissionTo from "../../../common/HasPermissionTo";
import { PAYMENT_THRESHOLD_LIMIT } from "../../../common/Constants";

function PaymentThreshold({ fromQuickstart, isNsc, hideOrShow, user, updateParentState, t, onSubmit, formRef, localizedInfo }) {
  const [editMode, setEditMode] = useState(fromQuickstart ? isNsc : false);
  const [paymentThreshold, setPaymentThreshold] = useState(undefined);
  const [paymentThresholdCopy, setPaymentThresholdCopy] = useState(undefined);
  const [saved, setSaved] = useState(false);
  const [maxLimit, setMaxLimit] = useState(0);
  const [maxLimitFormatted, setMaxLimitFormatted] = useState("");
  const [validationMaxLimitMsg, setValidationMaxLimitMsg] = useState("");
  const [validationInvalidFormat, setValidationInvalidFormat] = useState("");
  const [validationRequired, setValidationRequired] = useState("");

  const paymentThresholdRef = useRef();

  const minNumber = useCallback(() => {
    const localMarket = user.market;
    if (localMarket === "ZAF") {
      return 0;
    }
    return 1;
  }, [user.market]);

  const handleEdit = useCallback(() => {
    setEditMode(true);
    setSaved(false);
  }, []);

  const handleBlur = useCallback((e) => {
    if (e.target.value) {
      if (paymentThresholdRef.current.isValid()) {
        setEditMode(false);
      }
    }
  }, []);

  const handleChange = useCallback((event) => {
    setPaymentThreshold(event.target.value);
  }, []);

  const handleSubmit = useCallback(() => {
    hideOrShow(true);

    const paymentThresholdRequest = {
      paymentThreshold: paymentThreshold,
    };

    MarketPreferencesClient.savePaymentThresholdPreferences(
      user,
      paymentThresholdRequest
    )
      .then(() => {
        setEditMode(false);
        setPaymentThresholdCopy(paymentThreshold);
        setSaved(true);
        if (fromQuickstart)
          onSubmit({
            paymentThreshold: paymentThreshold,
          });
      })
      .finally(() => hideOrShow(false));
  }, [hideOrShow, paymentThreshold, user, fromQuickstart, onSubmit]);

  useEffect(() => {
    hideOrShow(true);
    MarketPreferencesClient.getPaymentThreshold(user)
      .then((response) => {
        const paymentThresholdFromDB = response.result.paymentThreshold;
        setPaymentThreshold(paymentThresholdFromDB || "");
        setPaymentThresholdCopy(paymentThresholdFromDB || "");
        updateParentState({
          paymentThreshold: paymentThresholdFromDB,
        });
      })
      .catch(() => {})
      .finally(() => {
        hideOrShow(false);
      });

    setValidationMaxLimitMsg(t("PaymentThreshold.validations.maxLimit") || "");
    setValidationInvalidFormat(t("PaymentThreshold.validations.invalidFormat") || "");
    setValidationRequired(t("PaymentThreshold.validations.required") || "");

    setMaxLimit(PAYMENT_THRESHOLD_LIMIT[user.market]
      ? PAYMENT_THRESHOLD_LIMIT[user.market].maxLimit
      : 99999.99);
    setMaxLimitFormatted(PAYMENT_THRESHOLD_LIMIT[user.market]
      ? PAYMENT_THRESHOLD_LIMIT[user.market].maxLimitFormatted
      : "99,999.99");
  }, [hideOrShow, user, updateParentState, t]);

  return (
    <div className="pageWrapper">
      <ValidatorForm onSubmit={handleSubmit} ref={formRef}>
        <div
          className={`${styles.subHeader} pagePaddingTop pagePaddingLeft pagePaddingRight`}
        >
          {t("PaymentThreshold.subheader")}
        </div>
        <div className="pagePaddingTop">
          <Grid
            container
            className={`${styles.tableRowHeader} pagePaddingLeft pagePaddingRight`}
          >
            <Grid item xs={6}>
              <div className={`${styles.thresholdInputGridLeftCell}`}>
                {t("PaymentThreshold.typeColumnCaption")}
              </div>
            </Grid>
            <Grid item xs={6}>
              <div className={styles.thresholdInputGridRightCell}>
                {t("PaymentThreshold.amountColumnCaption")}
                <HasPermissionTo
                  perform={["paymentThreshold:write"]}
                  permissions={user.permissions.rolePermissions}
                  condition={!editMode}
                  render={() => {
                    return (
                      <div className={styles.icon} onClick={handleEdit}>
                        <EditIcon id="PaymentThreshold" />
                      </div>
                    );
                  }}
                />
              </div>
            </Grid>
          </Grid>

          <Grid
            container
            className={`${styles.tableRowContent} pagePaddingRight`}
          >
            <Grid item xs={6}>
              <div className={`pagePaddingLeft ${styles.rowCellLeft}`}>
                {t("PaymentThreshold.rowDescription")}
              </div>
            </Grid>
            <Grid item xs={6}>
              <div
                className={`${styles.thresholdInputGridRightCell} ${styles.validatorCell}`}
              >
                {editMode ? (
                  <div className={styles.amountColumn}>
                    <div>
                      {localizedInfo.defaultCurrency.symbol}{" "}
                      &nbsp;&nbsp;
                    </div>
                    <TextValidator
                      fullWidth
                      onBlur={handleBlur}
                      ref={paymentThresholdRef}
                      data-testid="thresholdInputTestId"
                      name="thresholdInputName"
                      className={styles.inputField}
                      variant="outlined"
                      value={paymentThreshold || ""}
                      onChange={handleChange}
                      placeholder="-"
                      validators={[
                        "required",
                        "matchRegexp:^[0-9]*(\\.[0-9]{0,2})?$",
                        "minNumber:" + minNumber(),
                        "maxNumber:" + maxLimit,
                      ]}
                      errorMessages={[
                        validationRequired,
                        validationInvalidFormat,
                        validationInvalidFormat,
                        validationMaxLimitMsg +
                        " " +
                        maxLimitFormatted,
                      ]}
                    />
                  </div>
                ) : (
                  <div data-testid="thresholdText">
                    {paymentThreshold === ""
                      ? "-"
                      : localizedInfo.defaultCurrency.symbol}
                    &nbsp; {paymentThreshold}
                  </div>
                )}
              </div>
            </Grid>
          </Grid>
        </div>

        {fromQuickstart ? (
          ""
        ) : (
          <HasPermissionTo
            perform={["paymentThreshold:write"]}
            permissions={user.permissions.rolePermissions}
            render={() => {
              return saved ? (
                <div className={styles.saved}>
                  {t("PaymentThreshold.savedThreshold")}
                </div>
              ) : (
                <div className={styles.applyButton}>
                  <button
                    type="submit"
                    data-testid="thresholdSubmitBtn"
                    disabled={
                      paymentThreshold === paymentThresholdCopy
                    }
                  >
                    {t("OptIn.apply")}
                  </button>
                </div>
              );
            }}
          />
        )}
      </ValidatorForm>
    </div>
  );
}

export default withTranslation("emp")(PaymentThreshold);
